// src/pages/TeamPortfolio.jsx — Manager & Admin Team Portfolio
import React, { useEffect, useState, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Users, TrendingUp, UserCheck, UserX, Clock,
  BarChart3, Search, Award
} from 'lucide-react';
import './TeamPortfolio.css';

// ── Stat card ─────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, gradient, sub }) {
  return (
    <div className={`tp-stat-card ${gradient}`}>
      <Icon size={40} className="tp-stat-card__icon" />
      <div className="tp-stat-card__value">{value}</div>
      <div className="tp-stat-card__label">{label}</div>
      {sub != null && <div className="tp-stat-card__sub">{sub}</div>}
    </div>
  );
}

// ── Agent row ─────────────────────────────────────────────────
function AgentRow({ agent, rank }) {
  // Normalize: MySQL view returns totalLeads/activeLeads/lostLeads
  const total     = agent.totalLeads  ?? agent.total   ?? 0;
  const customers = agent.customers   ?? 0;
  const active    = agent.activeLeads ?? agent.active  ?? 0;
  const lost      = agent.lostLeads   ?? agent.lost    ?? 0;
  const rawConv   = total > 0 && !isNaN(customers) ? Math.round((Number(customers) / Number(total)) * 100) : 0;
  const convRate  = isNaN(rawConv) ? 0 : rawConv;

  return (
    <tr className="tp-agent-row">
      <td className="tp-rank fw-bold">
        #{rank}
      </td>
      <td>
        <div className="tp-agent-info">
          <div className="tp-agent-avatar">
            {(agent.agentName || agent.name || 'U').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="tp-agent-name">{agent.agentName || agent.name}</div>
            <div className="tp-agent-email">{agent.agentEmail || agent.email}</div>
          </div>
        </div>
      </td>
      <td className="tp-cell-num">{total}</td>
      <td className="tp-cell-num">
        <span className="tp-badge tp-badge--active">{active}</span>
      </td>
      <td className="tp-cell-num">
        <span className="tp-badge tp-badge--customer">{customers}</span>
      </td>
      <td className="tp-cell-num">
        <span className="tp-badge tp-badge--lost">{lost}</span>
      </td>
      <td className="tp-cell-num">
        <div className="tp-conv-bar-wrap">
          <div className="tp-conv-bar" style={{ width: `${convRate}%` }} />
          <span className="tp-conv-label">{convRate}%</span>
        </div>
      </td>
      <td className="tp-cell-num" style={{ color: 'var(--muted)', fontStyle: 'italic' }}>—</td>
    </tr>
  );
}

// ── Main component ────────────────────────────────────────────
export default function TeamPortfolio() {
  const { user }    = useAuth();
  const navigate    = useNavigate();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');

  useEffect(() => {
    if (!user || user.role === 'Agent') {
      navigate('/dashboard');
      return;
    }
    setLoading(true);
    api.get('/leads/portfolio/manager')
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load portfolio'))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const filteredAgents = useMemo(() => {
    if (!data?.agents) return [];
    const q = search.toLowerCase();
    const agents = q
      ? data.agents.filter(a =>
          (a.agentName || a.name || '').toLowerCase().includes(q) ||
          (a.agentEmail || a.email || '').toLowerCase().includes(q)
        )
      : data.agents;
    // Sort by customers descending
    return [...agents].sort((a, b) => (b.customers ?? 0) - (a.customers ?? 0));
  }, [data, search]);

  if (!user || user.role === 'Agent') return null;

  return (
    <div className="tp-page">
      {/* ── Page Subtitle Toolbar ── */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <span className="badge rounded-pill" style={{ background: 'var(--primary-light)', color: 'var(--primary)', border: '1px solid var(--primary-border)', padding: '6px 12px', fontSize: '12px', fontWeight: 600 }}>
          {user.role === 'Admin' ? 'All Company Teams' : 'My Managed Team'}
        </span>
        <span className="text-muted small d-none d-md-inline">
          {user.role === 'Admin' ? 'Company-wide agent conversion performance overview' : `Team performance tracking for ${user.name}`}
        </span>
      </div>

      {loading ? (
        <div className="tp-loading">
          <div className="tp-spinner" />
          <span>Loading portfolio…</span>
        </div>
      ) : !data ? (
        <div className="tp-error">Failed to load. Please refresh.</div>
      ) : (
        <>
          {/* ── Summary Stats ── */}
          <div className="tp-stats-grid">
            <StatCard icon={Users}     label="Total Agents"   value={data.summary.totalAgents}   gradient="grad-indigo" />
            <StatCard icon={BarChart3} label="Total Leads"    value={data.summary.totalLeads}    gradient="grad-violet" />
            <StatCard icon={UserCheck} label="Customers"      value={data.summary.totalCustomers} gradient="grad-emerald" />
            <StatCard icon={Clock}     label="Active Leads"   value={data.summary.totalActive}   gradient="grad-amber" />
            <StatCard icon={UserX}     label="Lost"           value={data.summary.totalLost}      gradient="grad-rose" />
            <StatCard icon={TrendingUp} label="Conversion Rate" value={`${data.summary.conversionRate}%`} gradient="grad-cyan" />
          </div>

          {/* ── Agent Table ── */}
          <div className="tp-card">
            <div className="tp-card__head">
              <div className="tp-card__title">
                <Award size={16} /> Agent Performance
              </div>
              <div className="tp-search-wrap">
                <Search size={14} className="tp-search-icon" />
                <input
                  className="tp-search-input"
                  placeholder="Search agent…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="tp-table-wrap">
              <table className="tp-table">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>#</th>
                    <th style={{ minWidth: 200 }}>Agent</th>
                    <th style={{ width: 90 }}>Total</th>
                    <th style={{ width: 90 }}>Active</th>
                    <th style={{ width: 100 }}>Customers</th>
                    <th style={{ width: 80 }}>Lost</th>
                    <th style={{ width: 140 }}>Conversion</th>
                    <th style={{ width: 110 }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAgents.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
                      No agents found.
                    </td></tr>
                  ) : filteredAgents.map((agent, i) => (
                    <AgentRow key={String(agent.agentId ?? agent.id)} agent={agent} rank={i + 1} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
